import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  viewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

import { LayoutStore } from '@twist/ui-core';

import { SidebarNavComponent } from '../sidebar-nav/sidebar-nav.component';

/**
 * Mobile slide-over drawer (TWS-108 Wave 6).
 *
 * Below the mobile breakpoint (<900 px — see {@link LayoutStore.viewportBelowBreakpoint})
 * the persistent sidebar column collapses to 0 and this component takes over
 * via a native `<dialog>` element. Opening is driven entirely by
 * {@link LayoutStore.mobileOpen}; consumers call `openMobile()` from the
 * topbar hamburger and the effect-based bridge below calls `dialog.showModal()`.
 *
 * The dialog re-hosts the very same `<app-sidebar-nav>` component, so there's
 * no duplicate nav markup. The sidebar's internal rail/popout behaviour is
 * driven by `LayoutStore.effectiveSidebarState()`, which already resolves to
 * `'hidden'` on mobile — inside the 300 px panel the sidebar falls back to
 * its full layout and the popout layer stays dormant.
 *
 * Auto-close triggers:
 *  - Viewport goes above 900 px (`viewportBelowBreakpoint()` flips to false).
 *  - Router emits `NavigationEnd` (user tapped a nav link).
 *  - User presses Escape — native `<dialog>`'s `cancel` event.
 *  - User clicks outside the panel (backdrop click).
 */
@Component({
  standalone: true,
  selector: 'app-mobile-sidebar-dialog',
  imports: [SidebarNavComponent],
  templateUrl: './mobile-sidebar-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MobileSidebarDialogComponent {
  protected readonly layoutStore = inject(LayoutStore);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  /** Signal-based `<dialog>` ref. `viewChild.required` is fine because the template always renders the element. */
  private readonly dialogRef = viewChild.required<ElementRef<HTMLDialogElement>>('dlg');

  constructor() {
    // Bridge: LayoutStore.mobileOpen() → dialog.showModal() / close().
    // `showModal()` is idempotent-safe via the `d.open` guard — calling it
    // on an already-open dialog throws `InvalidStateError`.
    effect(() => {
      const open = this.layoutStore.mobileOpen();
      const dialog = this.dialogRef().nativeElement;
      if (open && !dialog.open) {
        dialog.showModal();
      } else if (!open && dialog.open) {
        dialog.close();
      }
    });

    // Auto-close when the viewport grows past the mobile breakpoint — the
    // persistent sidebar column re-appears and the dialog would be redundant.
    effect(() => {
      if (!this.layoutStore.viewportBelowBreakpoint() && this.layoutStore.mobileOpen()) {
        this.layoutStore.closeMobile();
      }
    });

    // Auto-close on router navigation — tapping a nav link inside the
    // dialog should dismiss it. We listen for `NavigationEnd` (not `Start`)
    // so the route actually commits before dismissing.
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        if (this.layoutStore.mobileOpen()) {
          this.layoutStore.closeMobile();
        }
      });
  }

  /**
   * Backdrop click handler. The native `<dialog>` element is full-bleed
   * transparent; the visible panel is a child `<div>`. When the click
   * target is the `<dialog>` itself (not the panel), the user clicked
   * outside — close the drawer.
   */
  protected onBackdropClick(event: MouseEvent): void {
    if (event.target === this.dialogRef().nativeElement) {
      this.layoutStore.closeMobile();
    }
  }

  /**
   * Escape key handler. The native `<dialog>` fires `cancel` on Escape and
   * would then call `close()` itself — we `preventDefault()` so the store
   * stays the single source of truth, then flip `mobileOpen` false and let
   * the main effect drive the actual `close()` call.
   */
  protected onCancel(event: Event): void {
    event.preventDefault();
    this.layoutStore.closeMobile();
  }
}
